/**
 * API request builders for the two auth tiers (wiki/AUTH-DESIGN.md §"HTTP API
 * authorization").
 *
 *   - Guest routes (`/v1/guest/**`, AWS_IAM): SigV4-signed with Identity Pool
 *     guest credentials, via aws4fetch (~1KB, browser-native). The API is
 *     behind a custom domain, so service/region are set explicitly — aws4fetch
 *     cannot infer them from a non-`execute-api` host.
 *   - Authed routes (`/v1/**`, JWT): `Authorization: Bearer <id token>` (the
 *     backend reads the `email` claim, present only in the ID token) plus the
 *     `X-Identity-Id: <cached uid>` header the backend keys state by.
 *
 * Signing and header-attachment are split into pure builders so both are
 * unit-testable without hitting the network.
 */
import { AwsClient } from 'aws4fetch';
import { authConfig, type AuthConfig } from './config';
import type { AwsCredentials } from './types';

export type FetchImpl = typeof fetch;

const GUEST_SERVICE = 'execute-api';

function url(path: string, config: AuthConfig): string {
  return `${config.apiBaseUrl}${path}`;
}

/** Produce a SigV4-signed Request for a guest route (does not send it). */
export async function signGuestRequest(
  credentials: AwsCredentials,
  path: string,
  init?: RequestInit,
  config: AuthConfig = authConfig,
): Promise<Request> {
  const aws = new AwsClient({
    accessKeyId: credentials.accessKeyId,
    secretAccessKey: credentials.secretAccessKey,
    sessionToken: credentials.sessionToken,
    service: GUEST_SERVICE,
    region: config.region,
  });
  return aws.sign(url(path, config), init);
}

/** SigV4-sign and send a guest route request. */
export async function guestFetch(
  credentials: AwsCredentials,
  path: string,
  init?: RequestInit,
  config: AuthConfig = authConfig,
  fetchImpl: FetchImpl = fetch,
): Promise<Response> {
  const signed = await signGuestRequest(credentials, path, init, config);
  return fetchImpl(signed);
}

export interface AuthedRequest {
  idToken: string;
  identityId: string;
  path: string;
  init?: RequestInit;
}

/** Build the header set for an authed request: Bearer token + X-Identity-Id. */
export function buildAuthedHeaders(
  idToken: string,
  identityId: string,
  extra?: HeadersInit,
): Headers {
  const headers = new Headers(extra);
  headers.set('Authorization', `Bearer ${idToken}`);
  headers.set('X-Identity-Id', identityId);
  return headers;
}

/** Send an authed request with both required headers attached. */
export async function authedFetch(
  request: AuthedRequest,
  config: AuthConfig = authConfig,
  fetchImpl: FetchImpl = fetch,
): Promise<Response> {
  const headers = buildAuthedHeaders(
    request.idToken,
    request.identityId,
    request.init?.headers,
  );
  return fetchImpl(url(request.path, config), { ...request.init, headers });
}
